/**
 * SubjectReference — renders the "subject" of a timeline event's header.
 *
 * On a record's own timeline an event refers to that record with self-referential
 * copy ("this contact"). But causal-context nodes (isContext) are events from a
 * DIFFERENT entity, pulled into the timeline to explain a parent/child link — for
 * those, "this contact" is wrong (the subject is not the record being viewed), so
 * we render the subject as an EntityBadge instead. Falls back to the provided text
 * when the event is primary/on-timeline, or when the entity can't be resolved from
 * the lookup pool.
 */

import type { ReactNode } from 'react';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';

import { useViewStateLookups } from '../context/TimelineLookupsContext';

interface SubjectReferenceProps {
  /** True for de-emphasized causal-context nodes (event's entity ≠ current record). */
  isContext?: boolean;
  /** The event's own entity id — the subject to badge when off-timeline. */
  entityId: string;
  /** What to render on the record's own timeline (e.g. "this contact"). */
  fallback: ReactNode;
  /** Optional slideout push for the badge. */
  onEntityClick?: (entityId: string) => void;
}

export function SubjectReference({
  isContext,
  entityId,
  fallback,
  onEntityClick,
}: SubjectReferenceProps): ReactNode {
  const { lookups } = useViewStateLookups();
  const entity = isContext ? lookups.entities[entityId] : undefined;

  if (entity) {
    return (
      <EntityBadge
        state={{
          kind: 'data',
          data: {
            id: entity.entity_id,
            entityType: entity.entity_type_name,
            displayName: entity.ahoy_display_name ?? 'Unknown',
          },
        }}
        className='inline-flex align-middle'
        onClick={
          onEntityClick ? () => onEntityClick(entity.entity_id) : undefined
        }
      />
    );
  }

  return fallback;
}
